// New notifications collection: notifications/{uid}/items/{notificationId}
// This replaces profiles/{uid}/notifications for message notifications
import { ensureDb } from "./ensureDb";
import {
  collection,
  doc,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  getDocs,
  getDocsFromServer,
  writeBatch,
  limit,
  startAfter,
  type Query,
  type QueryDocumentSnapshot,
  type DocumentData,
  type SnapshotMetadata,
  serverTimestamp,
} from "firebase/firestore";
import { isNotificationRead } from "../notifications/presentation";

/** Page size for notifications list + “Load more” (incremental loading). */
export const NOTIFICATIONS_PAGE_SIZE = 20;

export interface MessageNotification {
  id: string;
  type: "message";
  threadId: string;
  fromUid: string;
  fromName: string;
  preview: string;
  createdAt: any;
  read: boolean;
}

export type NotificationsListPage = {
  items: MessageNotification[];
  hasMore: boolean;
  /** Last document of this page; pass as `cursor` to fetch the next page. */
  endCursor: QueryDocumentSnapshot<DocumentData> | null;
};

/**
 * One page of notifications for `notifications/{uid}/items`, newest first.
 * Uses `limit + 1` to detect `hasMore` without an extra count query.
 */
export async function fetchMyNotificationsPage(
  uid: string,
  pageSize: number,
  cursor?: QueryDocumentSnapshot<DocumentData> | null,
  opts?: { source?: "default" | "server" }
): Promise<NotificationsListPage> {
  const firestoreDb = ensureDb();
  const col = collection(firestoreDb, "notifications", uid, "items");
  const q = cursor
    ? query(col, orderBy("createdAt", "desc"), startAfter(cursor), limit(pageSize + 1))
    : query(col, orderBy("createdAt", "desc"), limit(pageSize + 1));
  const fetchFn = opts?.source === "server" ? getDocsFromServer : getDocs;
  const snap = await fetchFn(q);
  const docs = snap.docs;
  const hasMore = docs.length > pageSize;
  const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;
  const items = pageDocs.map((d) => ({
    id: d.id,
    ...(d.data() as Record<string, unknown>),
  })) as MessageNotification[];
  const endCursor = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1]! : null;
  return { items, hasMore, endCursor };
}

export async function listMyNotificationsNew(uid: string): Promise<MessageNotification[]> {
  const firestoreDb = ensureDb();
  try {
    const q = query(
      collection(firestoreDb, "notifications", uid, "items"),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as any),
    })) as MessageNotification[];
  } catch (err: any) {
    console.error("[listMyNotificationsNew] Error:", err);
    return [];
  }
}

/**
 * Subscribe to recent notifications (bounded). Badge/list previews only see newest `maxItems`;
 * full history pagination uses {@link fetchMyNotificationsPage}.
 */
export function listenMyNotifications(
  uid: string,
  onChange: (notifications: MessageNotification[], meta: SnapshotMetadata) => void,
  options?: { maxItems?: number }
): () => void {
  const firestoreDb = ensureDb();
  const maxItems = Math.min(Math.max(options?.maxItems ?? 100, 1), 200);
  const q = query(
    collection(firestoreDb, "notifications", uid, "items"),
    orderBy("createdAt", "desc"),
    limit(maxItems)
  );

  return onSnapshot(
    q,
    // The bell skips cached snapshots (UnreadCountsContext). When the cache already matches the server,
    // Firestore only says "now confirmed by the server" as a metadata change, which is not delivered
    // without this, so the bell (and the "(N) Arevina" tab title) stayed at 0 (found 2026-10-14).
    { includeMetadataChanges: true },
    (snapshot) => {
      const notifications = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as MessageNotification[];
      onChange(notifications, snapshot.metadata);
    },
    (err) => {
      console.error("[listenMyNotifications] Error:", err);
      onChange([], { fromCache: false, hasPendingWrites: false } as SnapshotMetadata);
    }
  );
}

/**
 * Unread count aligned with {@link listenMyNotifications} + {@link isNotificationRead}
 * (same window as nav badge listener; avoids mismatch with `where('read'==false)` vs legacy fields).
 */
export async function getUnreadNotificationsCountNew(uid: string): Promise<number> {
  const firestoreDb = ensureDb();
  const maxItems = 100;
  try {
    const q = query(
      collection(firestoreDb, "notifications", uid, "items"),
      orderBy("createdAt", "desc"),
      limit(maxItems)
    );
    let snap;
    try {
      snap = await getDocsFromServer(q);
    } catch {
      snap = await getDocs(q);
    }
    let unread = 0;
    for (const d of snap.docs) {
      const n = { id: d.id, ...d.data() } as Record<string, unknown>;
      if (!isNotificationRead(n)) unread++;
    }
    return unread;
  } catch (err: any) {
    if (import.meta.env.DEV) console.warn("[getUnreadNotificationsCountNew] Error:", err);
    return 0;
  }
}

/**
 * Mark notification as read
 */
export async function markNotificationReadNew(uid: string, notificationId: string): Promise<void> {
  const firestoreDb = ensureDb();
  try {
    await updateDoc(doc(firestoreDb, "notifications", uid, "items", notificationId), {
      read: true,
      readAt: serverTimestamp(),
    });
  } catch (err: any) {
    console.error("[markNotificationReadNew] Error:", err);
    throw err;
  }
}

/**
 * Mark all notifications as read
 */
const MARK_ALL_PAGE = 100;
const MARK_ALL_MAX_PAGES = 30;

/**
 * Mark every unread item the user can see in recent history (paginated by `createdAt`),
 * including legacy docs where `read` was never set (not matched by `where('read', '==', false)`).
 */
export async function markAllNotificationsReadNew(uid: string): Promise<void> {
  const firestoreDb = ensureDb();
  try {
    const col = collection(firestoreDb, "notifications", uid, "items");
    let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
    for (let page = 0; page < MARK_ALL_MAX_PAGES; page++) {
      const q: Query<DocumentData> = cursor
        ? query(col, orderBy("createdAt", "desc"), startAfter(cursor), limit(MARK_ALL_PAGE))
        : query(col, orderBy("createdAt", "desc"), limit(MARK_ALL_PAGE));
      let snap;
      try {
        snap = await getDocsFromServer(q);
      } catch {
        snap = await getDocs(q);
      }
      if (snap.empty) break;
      const batch = writeBatch(firestoreDb);
      const ts = serverTimestamp();
      let updates = 0;
      for (const d of snap.docs) {
        const row: { id: string; read?: unknown; readAt?: unknown } = {
          id: d.id,
          ...(d.data() as { read?: unknown; readAt?: unknown }),
        };
        if (!isNotificationRead(row)) {
          batch.update(doc(firestoreDb, "notifications", uid, "items", d.id), {
            read: true,
            readAt: ts,
          });
          updates++;
        }
      }
      if (updates > 0) await batch.commit();
      cursor = (snap.docs[snap.docs.length - 1] ?? null) as QueryDocumentSnapshot<DocumentData> | null;
      if (snap.size < MARK_ALL_PAGE) break;
    }
  } catch (err: any) {
    console.error("[markAllNotificationsReadNew] Error:", err);
    throw err;
  }
}
